import React, { useId, useState } from "react";
import {
    FiAward,
    FiArrowDown,
    FiArrowUp,
    FiBookmark,
    FiCheckCircle,
    FiCornerUpLeft,
    FiEdit2,
    FiFlag,
    FiTrash2,
    FiUser,
} from "react-icons/fi";

import { IckonicLogoIcon } from "../../../Assets/SVGs/Logos";
import { COMMENT_REPORTING_UI_ENABLED } from "../../../Constants/FeatureFlags";
import { PopupType, usePopup } from "../../../Context/PopupContext";
import { describeVideoCommentError } from "../../../Hooks/Comments/Video/useVideoComments";
import type { VideoCommentsController } from "../../../Hooks/Comments/Video/useVideoComments";
import { VideoCommentDTO } from "../../../Models/DTOs/VideoCommentsDTO";
import type { DeleteVideoCommentPopupData } from "./DeleteVideoCommentPopup";
import ReportVideoCommentDialog from "./ReportVideoCommentDialog";
import VideoCommentComposer from "./VideoCommentComposer";

interface VideoCommentCardProps {
    comment: VideoCommentDTO;
    isRoot?: boolean;
    showPinnedBadge?: boolean;
    depth?: number;
    controller: VideoCommentsController;
    onFocusChange?: (focused: boolean) => void;
}

interface CommentRankIconProps {
    className: string;
    label: string;
    tooltipId: string;
    children: React.ReactNode;
}

const CommentRankIcon: React.FC<CommentRankIconProps> = ({
    className,
    label,
    tooltipId,
    children,
}) => (
    <span
        className={`video-comment-card__rank-icon ${className}`}
        role="img"
        aria-label={label}
        aria-describedby={tooltipId}
        tabIndex={0}
    >
        <span className="video-comment-card__rank-glyph" aria-hidden="true">
            {children}
        </span>
        <span
            id={tooltipId}
            className="video-comment-card__rank-tooltip"
            role="tooltip"
        >
            {label}
        </span>
    </span>
);

const relativeDate = (raw: string): string => {
    const date = new Date(raw);
    const elapsedSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const absolute = Math.abs(elapsedSeconds);
    const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (absolute < 60) return formatter.format(elapsedSeconds, "second");
    if (absolute < 3600) return formatter.format(Math.round(elapsedSeconds / 60), "minute");
    if (absolute < 86400) return formatter.format(Math.round(elapsedSeconds / 3600), "hour");
    if (absolute < 2592000) return formatter.format(Math.round(elapsedSeconds / 86400), "day");
    return date.toLocaleDateString();
};

const initialsFor = (displayName: string): string => {
    const words = displayName.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "I";
    const first = Array.from(words[0])[0] ?? "I";
    const last = words.length > 1 ? Array.from(words[words.length - 1])[0] ?? "" : "";
    return `${first}${last}`.toLocaleUpperCase();
};

const VideoCommentCard: React.FC<VideoCommentCardProps> = ({
    comment,
    isRoot = false,
    showPinnedBadge = true,
    depth = 0,
    controller,
    onFocusChange,
}) => {
    const [replying, setReplying] = useState(false);
    const [editing, setEditing] = useState(false);
    const [reporting, setReporting] = useState(false);
    const [votePending, setVotePending] = useState(false);
    const [pinPending, setPinPending] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const replyComposerId = useId();
    const { openPopup } = usePopup();

    const submitReply = async (body: string, idempotencyKey: string) => {
        await controller.createComment(body, idempotencyKey, comment.id);
        setReplying(false);
    };

    const submitEdit = async (body: string) => {
        await controller.editComment(comment.id, body, comment.version);
        setEditing(false);
    };

    const toggleVote = async (desired: 1 | -1) => {
        if (votePending || !controller.state.permissions.can_vote) return;
        setVotePending(true);
        setActionError(null);
        try {
            await controller.setVote(
                comment.id,
                comment.viewer_vote === desired ? null : desired
            );
        } catch (error) {
            setActionError(describeVideoCommentError(error, controller.contentType));
        } finally {
            setVotePending(false);
        }
    };

    const togglePin = async () => {
        if (pinPending) return;
        setPinPending(true);
        setActionError(null);
        try {
            if (comment.is_pinned) await controller.unpinComment(comment.id);
            else await controller.pinComment(comment.id);
        } catch (error) {
            setActionError(describeVideoCommentError(error, controller.contentType));
        } finally {
            setPinPending(false);
        }
    };

    const displayName = comment.author?.display_name || "Ickonic member";
    const isModeratorDelete = controller.isModerator && !comment.author?.is_viewer;
    const avatarText = comment.is_deleted ? "\u2014" : initialsFor(displayName);
    const isOfficial = !comment.is_deleted && !!comment.author?.is_super_user;
    const isLifetimeMember =
        !comment.is_deleted && !!comment.author?.is_lifetime_member;
    const identityPresentationClass = isOfficial
        ? "is-super-user"
        : isLifetimeMember
        ? "is-lifetime-member"
        : "";
    const identityTier = comment.is_deleted
        ? "deleted"
        : isOfficial
        ? "official"
        : isLifetimeMember
        ? "lifetime"
        : "member";

    const visualDepth = Math.min(Math.max(depth, 1), 4);

    const openDeletePopup = () => {
        const data: DeleteVideoCommentPopupData = {
            commentId: comment.id,
            requiresReason: isModeratorDelete,
            onDelete: (reason) =>
                controller.deleteComment(comment.id, comment.version, reason),
            onFocusChange,
        };
        openPopup(PopupType.COMMENT_DELETE, data);
    };

    return (
        <article
            id={`comment-${comment.id}`}
            className={`video-comment-card ${isRoot ? "is-root" : "is-reply"} ${
                comment.is_deleted ? "is-deleted" : ""
            } ${comment.is_pinned ? "is-pinned" : ""}`}
            style={
                isRoot
                    ? undefined
                    : ({ "--comment-depth": visualDepth } as React.CSSProperties)
            }
            data-comment-id={comment.id}
            data-comment-depth={isRoot ? undefined : visualDepth}
        >
            <div
                className="video-comment-card__identity-stack"
                data-author-tier={identityTier}
            >
                <div
                    className={`video-comment-card__avatar ${identityPresentationClass}`}
                    aria-hidden="true"
                >
                    {avatarText}
                </div>

                {!comment.is_deleted && (
                    <div className="video-comment-card__rank-list">
                        {!isOfficial && !isLifetimeMember && (
                            <CommentRankIcon
                                className="is-member"
                                label="Member"
                                tooltipId={`comment-${comment.id}-member-rank`}
                            >
                                <FiUser />
                            </CommentRankIcon>
                        )}
                        {isOfficial && (
                            <CommentRankIcon
                                className="is-official"
                                label="Ickonic Staff"
                                tooltipId={`comment-${comment.id}-official-rank`}
                            >
                                <IckonicLogoIcon />
                            </CommentRankIcon>
                        )}
                        {!isOfficial && isLifetimeMember && (
                            <CommentRankIcon
                                className="is-lifetime-member"
                                label="Lifetime member"
                                tooltipId={`comment-${comment.id}-lifetime-rank`}
                            >
                                <FiAward />
                            </CommentRankIcon>
                        )}
                    </div>
                )}
            </div>

            <div className="video-comment-card__content">
                <header className="video-comment-card__header">
                    <div className="video-comment-card__identity">
                        <strong>
                            {comment.is_deleted ? "Deleted comment" : displayName}
                        </strong>
                        {comment.author?.is_viewer && (
                            <span className="video-comment-card__you">You</span>
                        )}
                        {comment.is_pinned && showPinnedBadge && (
                            <span className="video-comment-card__badge">
                                <FiBookmark aria-hidden="true" /> Pinned
                            </span>
                        )}
                    </div>
                    <div className="video-comment-card__meta">
                        <time
                            dateTime={comment.created_at}
                            title={new Date(comment.created_at).toLocaleString()}
                        >
                            {relativeDate(comment.created_at)}
                        </time>
                        {comment.is_edited && <span>Edited</span>}
                    </div>
                </header>

                {editing && comment.body !== null ? (
                    <VideoCommentComposer
                        initialBody={comment.body}
                        submitLabel="Save changes"
                        autoFocus
                        onSubmit={(body) => submitEdit(body)}
                        onCancel={() => setEditing(false)}
                        onFocusChange={onFocusChange}
                    />
                ) : (
                    <p className="video-comment-card__body">
                        {comment.is_deleted || comment.body === null
                            ? "This comment was deleted."
                            : comment.body}
                    </p>
                )}

                {!comment.is_deleted && !editing && (
                    <div className="video-comment-card__actions">
                        <div
                            className="video-comment-card__votes"
                            aria-label="Comment votes"
                        >
                            <button
                                type="button"
                                aria-label="Upvote comment"
                                aria-describedby={`comment-${comment.id}-upvotes`}
                                aria-pressed={comment.viewer_vote === 1}
                                title={`${comment.upvotes.toLocaleString()} upvotes`}
                                disabled={
                                    votePending || !controller.state.permissions.can_vote
                                }
                                onClick={() => void toggleVote(1)}
                            >
                                <FiArrowUp aria-hidden="true" />
                            </button>
                            <span
                                id={`comment-${comment.id}-upvotes`}
                                className="video-comments__visually-hidden"
                            >
                                {comment.upvotes.toLocaleString()} {comment.upvotes === 1 ? "upvote" : "upvotes"}
                            </span>
                            <span
                                className="video-comment-card__score"
                                aria-label={`Comment score ${comment.score}`}
                            >
                                {comment.score.toLocaleString()}
                            </span>
                            <button
                                type="button"
                                aria-label="Downvote comment"
                                aria-describedby={`comment-${comment.id}-downvotes`}
                                aria-pressed={comment.viewer_vote === -1}
                                title={`${comment.downvotes.toLocaleString()} downvotes`}
                                disabled={
                                    votePending || !controller.state.permissions.can_vote
                                }
                                onClick={() => void toggleVote(-1)}
                            >
                                <FiArrowDown aria-hidden="true" />
                            </button>
                            <span
                                id={`comment-${comment.id}-downvotes`}
                                className="video-comments__visually-hidden"
                            >
                                {comment.downvotes.toLocaleString()} {comment.downvotes === 1 ? "downvote" : "downvotes"}
                            </span>
                        </div>

                        {controller.state.permissions.can_comment && (
                            <button
                                type="button"
                                className="video-comment-card__action is-prominent"
                                aria-expanded={replying}
                                aria-controls={replyComposerId}
                                onClick={() => setReplying((value) => !value)}
                            >
                                <FiCornerUpLeft aria-hidden="true" />
                                <span>Reply</span>
                            </button>
                        )}

                        <div className="video-comment-card__secondary-actions">
                            {comment.can_edit && (
                                <button
                                    type="button"
                                    className="video-comment-card__action"
                                    onClick={() => setEditing(true)}
                                >
                                    <FiEdit2 aria-hidden="true" />
                                    <span>Edit</span>
                                </button>
                            )}
                            {comment.can_delete && (
                                <button
                                    type="button"
                                    className="video-comment-card__action is-danger"
                                    onClick={openDeletePopup}
                                >
                                    <FiTrash2 aria-hidden="true" />
                                    <span>Delete</span>
                                </button>
                            )}
                            {COMMENT_REPORTING_UI_ENABLED &&
                                comment.can_report &&
                                !controller.state.reportedCommentIds[comment.id] && (
                                    <button
                                        type="button"
                                        className="video-comment-card__action"
                                        onClick={() => setReporting(true)}
                                    >
                                        <FiFlag aria-hidden="true" />
                                        <span>Report</span>
                                    </button>
                                )}
                            {COMMENT_REPORTING_UI_ENABLED &&
                                controller.state.reportedCommentIds[comment.id] && (
                                <span className="video-comment-card__reported">
                                    <FiCheckCircle aria-hidden="true" /> Reported
                                </span>
                                )}
                            {controller.state.permissions.can_pin && isRoot && (
                                <button
                                    type="button"
                                    className="video-comment-card__action"
                                    disabled={pinPending}
                                    onClick={() => void togglePin()}
                                >
                                    <FiBookmark aria-hidden="true" />
                                    <span>
                                        {pinPending
                                            ? "Updating…"
                                            : comment.is_pinned
                                            ? "Unpin"
                                            : "Pin"}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {replying && (
                    <div
                        id={replyComposerId}
                        className="video-comment-card__inline-composer"
                    >
                        <VideoCommentComposer
                            placeholder={`Reply to ${displayName}…`}
                            submitLabel="Post reply"
                            autoFocus
                            onSubmit={submitReply}
                            onCancel={() => setReplying(false)}
                            onFocusChange={onFocusChange}
                        />
                    </div>
                )}

                {actionError && (
                    <p className="video-comments__form-error" role="alert">
                        {actionError}
                    </p>
                )}
            </div>

            {COMMENT_REPORTING_UI_ENABLED && reporting && (
                <ReportVideoCommentDialog
                    onClose={() => setReporting(false)}
                    onSubmit={(request) => controller.reportComment(comment.id, request)}
                    onFocusChange={onFocusChange}
                />
            )}
        </article>
    );
};

export default VideoCommentCard;
